import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { vapi } from './context/TMSContext';

import FleetGPS from './rooms/FleetGPS';
import Fuel from './rooms/Fuel';
import MaintenanceHub from './rooms/MaintenanceHub';
import CRM from './rooms/CRM';
import ComplianceRoom from './rooms/ComplianceRoom';
import AssetsEquipment from './rooms/AssetsEquipment';
import Accounting from './rooms/Accounting';
import DriverManagement from './rooms/DriverManagement';
import ControlTower from './rooms/ControlTower';
import CommandCenter from './rooms/CommandCenter';
import Settings from './rooms/Settings';
import Financials from './rooms/Financials';

// Extend window for Google Maps JS API
declare global { interface Window { google: any; } }

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || "",
  process.env.REACT_APP_SUPABASE_ANON_KEY || ""
);
const JAKE_ID = process.env.REACT_APP_VAPI_ASSISTANT_ID || "";

// ─── STYLES ───────────────────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = { background:"#000", border:"1px solid #1a1a1a", color:"#fff", padding:"10px", fontFamily:"monospace", width:"100%", boxSizing:"border-box", fontSize:"12px" };
const voiceOff: React.CSSProperties = { width:"100%", background:"transparent", color:"#32CD32", border:"1px solid #32CD32", padding:"10px", cursor:"pointer", fontSize:"11px", letterSpacing:"1px", fontFamily:"monospace" };
const voiceOn: React.CSSProperties = { width:"100%", background:"#32CD32", color:"#000", border:"none", padding:"10px", fontWeight:"bold", fontFamily:"monospace", fontSize:"11px", letterSpacing:"1px" };
const smallBtn: React.CSSProperties = { background:"#32CD32", color:"#000", border:"none", padding:"8px 16px", fontWeight:"bold", cursor:"pointer", fontSize:"10px", fontFamily:"monospace", letterSpacing:"1px" };

export default function App() {

  // ─── AUTH ─────────────────────────────────────────────────────────────────
  const [session, setSession] = useState<any>(null);
  const [checkingSession, setCheckingSession] = useState(true);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string>("");
  const [companyName, setCompanyName] = useState<string>("");

  // ─── JAKE ─────────────────────────────────────────────────────────────────
  const [isJakeActive, setIsJakeActive] = useState(false);
  const [jakeAlerts, setJakeAlerts] = useState<Array<{id:string;level:'RED'|'YELLOW'|'GREEN';message:string;load_number?:string;driver_name?:string;timestamp:Date;acknowledged:boolean}>>([]);

  // ─── NAVIGATION ───────────────────────────────────────────────────────────
  const [room, setRoom] = useState('Command Center');
  const [data, setData] = useState<any[]>([]);
  const [selectedLoad, setSelectedLoad] = useState<any>(null);
  const [showEntryForm, setShowEntryForm] = useState(false);

  // ─── SHARED DATA ──────────────────────────────────────────────────────────
  const [crmContacts, setCrmContacts] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [assets, setAssets] = useState<any[]>([]);

  // ─── FORM STATE (owned here, passed as props to rooms) ────────────────────
  const emptyLoad = { load_number:"",customer_name:"",equipment_type:"",status:"ACTIVE",commodity:"",weight_lbs:0,po_number:"",special_instructions:"",pickup_date:"",pickup_city:"",pickup_state:"",shipper_name:"",shipper_address:"",shipper_phone:"",shipper_email:"",shipper_appt_required:false,shipper_hours:"",delivery_date:"",delivery_city:"",delivery_state:"",consignee_name:"",consignee_address:"",consignee_phone:"",consignee_email:"",consignee_appt_required:false,consignee_hours:"",rate_total:0,loaded_miles:0,deadhead_miles:0,fuel_cost_per_mile:0,driver_pay_per_mile:0,fixed_cost_per_mile:0,assigned_driver_id:"" };
  const emptyDriver = { full_name:"",date_of_birth:"",date_of_hire:"",phone:"",email:"",status:"ACTIVE",home_address:"",home_city:"",home_state:"",home_zip:"",emergency_contact_name:"",emergency_contact_phone:"",emergency_contact_relationship:"",cdl_number:"",cdl_state:"",cdl_class:"",cdl_expiration:"",cdl_endorsements:[] as string[],cdl_restrictions:"",dot_physical_expiration:"",drug_test_date:"",drug_test_result:"",drug_test_type:"",alcohol_test_date:"",alcohol_test_result:"",mvr_date:"",mvr_result:"",clearinghouse_enrolled:false,clearinghouse_query_date:"",clearinghouse_status:"",random_testing_enrolled:false,random_testing_pool:"",psp_date:"",psp_result:"",annual_review_date:"",next_annual_review_due:"",hos_status:"OFF DUTY",current_city:"",current_state:"",pay_type:"PERCENT",pay_rate_percent:0,pay_rate_per_mile:0,notes:"" };
  const emptyAsset = { unit_number:"",asset_type:"",make_model:"",year:"",vin:"",license_plate:"",license_state:"",dot_number:"",current_status:"AVAILABLE",odometer:0,last_inspection_date:"",next_inspection_due:"",registration_expiration:"",insurance_expiration:"",notes:"" };
  const emptyCompliance = { document_type:"CDL",document_name:"",driver_id:"",related_asset_id:"",issue_date:"",expiration_date:"",reference_number:"",status:"CURRENT",notes:"" };
  const emptyAccounting = { entry_type:"INVOICE",load_id:"",driver_id:"",amount:0,date:"",status:"PENDING",reference_number:"",payment_method:"",notes:"" };
  const emptyFuel = { truck_number:"",driver_id:"",location_city:"",location_state:"",gallons:0,price_per_gallon:0,total_cost:0,odometer:0,fuel_card:"",notes:"" };
  const emptyMaintenance = { unit_number:"",vin:"",service_type:"Oil Change",last_service:"",next_service:"",mileage_at_service:0,vendor:"",cost:0,status:"COMPLETED",notes:"" };

  const [newLoad, setNewLoad] = useState(emptyLoad);
  const [newDriver, setNewDriver] = useState(emptyDriver);
  const [newAsset, setNewAsset] = useState(emptyAsset);
  const [newCompliance, setNewCompliance] = useState(emptyCompliance);
  const [newAccounting, setNewAccounting] = useState(emptyAccounting);
  const [newFuel, setNewFuel] = useState(emptyFuel);
  const [newMaintenance, setNewMaintenance] = useState(emptyMaintenance);
  const [crmDraft, setCrmDraft] = useState({ entity_name:"",city:"",state:"",entity_type:"SHIPPER",status:"NEW",contact_person:"",email:"",phone:"" });

  // ─── JAKE ALERT SYSTEM ────────────────────────────────────────────────────
  const addJakeAlert = useCallback((alert: {level:'RED'|'YELLOW'|'GREEN';message:string;load_number?:string;driver_name?:string}) => {
    const id = `alert_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setJakeAlerts(prev => {
      // Deduplicate: skip if same unacknowledged alert already exists for this load + message
      if (prev.some(a => !a.acknowledged && a.load_number === alert.load_number && a.message === alert.message)) return prev;
      return [{ ...alert, id, timestamp: new Date(), acknowledged: false }, ...prev].slice(0, 50);
    });
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = alert.level === 'RED' ? 880 : 660; osc.type = 'sine';
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.0);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 1.0);
    } catch {}
  }, []);

  const acknowledgeAlert = (id: string) => {
    setJakeAlerts(prev => prev.map(a => a.id === id ? { ...a, acknowledged: true } : a));
  };

  // ─── JAKE TOGGLE ──────────────────────────────────────────────────────────
  const toggleJake = async () => {
    if (isJakeActive) { vapi.stop(); return; }
    if (!JAKE_ID) { alert("Vapi Assistant ID missing — check REACT_APP_VAPI_ASSISTANT_ID in .env"); return; }
    if (!companyId) { alert("Company not loaded yet — wait a moment and try again."); return; }
    console.log('Jake: starting call with assistant', JAKE_ID, 'vapi instance:', vapi);
    try {
      const call = await (vapi as any).start(JAKE_ID, {
        metadata: { companyId },
        firstMessage: `Jake here. I'm watching your loads, drivers, and compliance. What do you need?`,
      });
      console.log('Jake: start returned', call);
      if (!call) alert('Jake could not connect. Check your microphone permissions and make sure the browser allows mic access on localhost.');
    } catch (err: any) {
      console.error('Jake start error:', err);
      alert(`Jake error: ${err?.message || JSON.stringify(err) || 'unknown error'}. Check mic permissions.`);
    }
  };

  // ─── JAKE BACKGROUND MONITORS ─────────────────────────────────────────────

  // Check if Jake already sent an alert for this load+action in the last 24h (prevents spam)
  const alreadyActed = useCallback(async (loadNumber: string, actionType: string): Promise<boolean> => {
    const since = new Date(Date.now() - 24 * 3600000).toISOString();
    const { data } = await supabase.from('jake_actions_log').select('id').eq('company_id', companyId || '').eq('load_number', String(loadNumber)).eq('action_type', actionType).gte('created_at', since).maybeSingle();
    return !!data;
  }, [companyId]);

  const logAction = useCallback(async (loadNumber: string, actionType: string, message: string, level = 'YELLOW') => {
    try { await supabase.from('jake_actions_log').insert({ company_id: companyId, load_number: String(loadNumber), action_type: actionType, message, level, created_at: new Date().toISOString() }); } catch { /* non-critical */ }
  }, [companyId]);

  // Haversine distance in miles
  const distMiles = (lat1: number, lng1: number, lat2: number, lng2: number) => {
    if (!lat1 || !lng1 || !lat2 || !lng2) return null;
    const R = 3959, dLat = (lat2-lat1)*Math.PI/180, dLng = (lng2-lng1)*Math.PI/180;
    const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLng/2)**2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const runDelayMonitor = useCallback(async () => {
    if (!companyId) return;
    const { data: loads } = await supabase.from('loads')
      .select('id,load_number,status,pickup_date,delivery_date,delivery_city,delivery_state,delivery_lat,delivery_lng,assigned_driver_id,customer_name,broker_email,rate_total')
      .eq('company_id', companyId).in('status', ['ACTIVE','IN TRANSIT']);
    if (!loads) return;
    const today = new Date().toISOString().split('T')[0];
    const nowMs = Date.now();

    // Auto-clear pickup alerts for loads now IN TRANSIT
    const pickedUpNums = new Set(loads.filter((l: any) => l.status === 'IN TRANSIT').map((l: any) => String(l.load_number)));
    setJakeAlerts(prev => prev.map(a =>
      !a.acknowledged && a.load_number && pickedUpNums.has(String(a.load_number)) && a.message.includes('pickup')
        ? { ...a, acknowledged: true } : a
    ));

    for (const l of loads) {
      const loadNum = String(l.load_number);

      // ── Past-due delivery (reactive) ──
      if (l.delivery_date && l.delivery_date < today) {
        const hoursLate = Math.round((nowMs - new Date(l.delivery_date + 'T23:59:00').getTime()) / 3600000);
        addJakeAlert({ level:'RED', message:`Load #${loadNum} delivery overdue by ~${hoursLate}h — was due ${l.delivery_date} in ${l.delivery_city}, ${l.delivery_state}`, load_number:loadNum });

        // Auto-email broker if not done in last 24h
        const acted = await alreadyActed(loadNum, 'auto_delay_email');
        if (!acted) {
          const brokerEmail = l.broker_email || await (async () => {
            if (!l.customer_name) return null;
            const { data: c } = await supabase.from('crm_contacts').select('email').eq('company_id', companyId || '').ilike('entity_name', `%${l.customer_name}%`).maybeSingle();
            return c?.email || null;
          })();
          if (brokerEmail) {
            await supabase.functions.invoke('jake-delay-email', { body: { load: { ...l, broker_email: brokerEmail }, driverName: 'Driver', delayType: 'DELIVERY', hoursOverdue: hoursLate } });
            await logAction(loadNum, 'auto_delay_email', `Jake auto-emailed broker about overdue delivery on Load #${loadNum}`, 'RED');
          }
          // Push driver: "you're running late"
          if (l.assigned_driver_id) {
            const driverActed = await alreadyActed(loadNum, 'auto_driver_late_push');
            if (!driverActed) {
              await supabase.functions.invoke('push-notify', { body: { driverId: l.assigned_driver_id, title: `⚠ Load #${loadNum} Running Late`, body: `Your delivery to ${l.delivery_city}, ${l.delivery_state} was due ${l.delivery_date}. Jake is notifying the broker. What's your ETA?`, url: '/driver' } });
              await logAction(loadNum, 'auto_driver_late_push', `Jake pushed late alert to driver on Load #${loadNum}`, 'RED');
            }
          }
        }
      }

      // ── Past-due pickup (reactive) ──
      else if (l.pickup_date && l.pickup_date < today && l.status === 'ACTIVE') {
        addJakeAlert({ level:'YELLOW', message:`Load #${loadNum} pickup date passed — not yet dispatched`, load_number:loadNum });
      }

      // ── Predictive: IN TRANSIT loads due today, check if driver can make it ──
      else if (l.status === 'IN TRANSIT' && l.delivery_date === today && l.assigned_driver_id) {
        const { data: driver } = await supabase.from('drivers').select('full_name,last_lat,last_lng,hos_drive_remaining_sec').eq('id', l.assigned_driver_id).maybeSingle();
        if (driver?.last_lat && l.delivery_lat) {
          const miles = distMiles(Number(driver.last_lat), Number(driver.last_lng), Number(l.delivery_lat), Number(l.delivery_lng));
          if (miles !== null) {
            const etaHours = miles / 55; // 55mph average
            const hoursUntilEOD = (new Date(l.delivery_date + 'T23:59:00').getTime() - nowMs) / 3600000;
            const hosHours = Number(driver.hos_drive_remaining_sec || 0) / 3600;
            const willBeLate = etaHours > hoursUntilEOD || hosHours < etaHours;
            if (willBeLate) {
              const reason = hosHours < etaHours ? 'HOS will run out before delivery' : `ETA ~${Math.round(etaHours)}h but only ${Math.round(hoursUntilEOD)}h until cutoff`;
              addJakeAlert({ level:'YELLOW', message:`Load #${loadNum} at risk — ${driver.full_name} is ~${Math.round(miles)} mi from ${l.delivery_city} (${reason})`, load_number:loadNum });
              const acted = await alreadyActed(loadNum, 'auto_at_risk_push');
              if (!acted) {
                await supabase.functions.invoke('push-notify', { body: { driverId: l.assigned_driver_id, title: `Load #${loadNum} — Delivery at Risk`, body: `Jake estimates you're ~${Math.round(miles)} mi from ${l.delivery_city}. Are you going to make the delivery window? Reply with your ETA.`, url: '/driver' } });
                await logAction(loadNum, 'auto_at_risk_push', `Jake sent at-risk push to ${driver.full_name} on Load #${loadNum}`, 'YELLOW');
              }
            }
          }
        }
      }
    }
  }, [companyId, addJakeAlert, alreadyActed, logAction, distMiles]);

  const runComplianceMonitor = useCallback(async () => {
    if (!companyId) return;
    const { data: drvs } = await supabase.from('drivers').select('full_name,cdl_expiration,dot_physical_expiration').eq('company_id', companyId);
    if (!drvs) return;
    const today = new Date(); const in7 = new Date(today); in7.setDate(today.getDate()+7); const in30 = new Date(today); in30.setDate(today.getDate()+30);
    for (const d of drvs) {
      if (d.cdl_expiration) { const exp=new Date(d.cdl_expiration); if(exp<=in7) addJakeAlert({level:'RED',message:`${d.full_name}'s CDL expires ${d.cdl_expiration} — URGENT`,driver_name:d.full_name}); else if(exp<=in30) addJakeAlert({level:'YELLOW',message:`${d.full_name}'s CDL expires ${d.cdl_expiration}`,driver_name:d.full_name}); }
      if (d.dot_physical_expiration) { const exp=new Date(d.dot_physical_expiration); if(exp<=in7) addJakeAlert({level:'RED',message:`${d.full_name}'s DOT Physical expires ${d.dot_physical_expiration} — URGENT`,driver_name:d.full_name}); else if(exp<=in30) addJakeAlert({level:'YELLOW',message:`${d.full_name}'s DOT Physical expires ${d.dot_physical_expiration}`,driver_name:d.full_name}); }
    }
  }, [companyId, addJakeAlert]);

  // ─── ROOM DATA ────────────────────────────────────────────────────────────
  const ROOM_TABLE: Record<string,string> = { "Control Tower":"loads","CRM":"crm_contacts","Driver Management":"drivers","Maintenance Hub":"maintenance","Compliance Room":"compliance","Accounting":"accounting","Fuel":"fuel_logs","Assets / Equipment":"assets" };

  const fetchRoomData = useCallback(async () => {
    const table = ROOM_TABLE[room];
    if (!table || !companyId) { setData([]); return; }
    const { data: result, error } = await supabase.from(table).select("*").eq("company_id", companyId).order("created_at", { ascending: false });
    if (error) { console.error("Fetch room error:", error.message); return; }
    setData(result || []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, companyId]);

  const fetchCrmContacts = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase.from("crm_contacts").select("id,entity_name,entity_type,contact_person,email,phone,city,state").eq("company_id", companyId).order("entity_name", { ascending: true });
    if (!error) setCrmContacts(data || []);
  }, [companyId]);

  const fetchDrivers = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase.from("drivers").select("id,full_name,status,current_city,current_state,hos_status,pay_rate_percent,pay_percent,pay_type,pay_rate_per_mile").eq("company_id", companyId);
    if (!error) setDrivers(data || []);
  }, [companyId]);

  const fetchAssets = useCallback(async () => {
    if (!companyId) return;
    const { data, error } = await supabase.from("assets").select("id,unit_number,asset_type,current_status,vin,make_model").eq("company_id", companyId);
    if (!error) setAssets(data || []);
  }, [companyId]);

  // ─── CRM EXPORT ───────────────────────────────────────────────────────────
  const exportCrmCsv = () => {
    if (!data || data.length === 0) { alert('No CRM contacts to export.'); return; }
    const headers = ['Company Name','Type','City','State','Contact Person','Email','Phone','Status'];
    const rows = data.map((c:any) => [`"${(c.entity_name||'').replace(/"/g,'""')}"`,c.entity_type||'',c.city||'',c.state||'',`"${(c.contact_person||'').replace(/"/g,'""')}"`,c.email||'',c.phone||'',c.status||'NEW'].join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download=`blacktop-crm-${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  // ─── AUTH + COMPANY EFFECTS ───────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setCheckingSession(false); });
    const { data: listener } = supabase.auth.onAuthStateChange((_e, s) => { setSession(s); });
    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;
    // Try company_members first, fall back to profiles
    supabase.from("company_members").select("company_id").eq("user_id", session.user.id).maybeSingle()
      .then(({ data }) => {
        if (data?.company_id) { setCompanyId(data.company_id); return; }
        // Fallback: profiles table
        supabase.from("profiles").select("company_id").eq("user_id", session.user.id).maybeSingle()
          .then(({ data: p }) => { if (p?.company_id) setCompanyId(p.company_id); });
      });
    supabase.from("profiles").select("display_name").eq("user_id", session.user.id).maybeSingle().then(({ data }) => { setDisplayName(data?.display_name || ""); });
  }, [session]);

  useEffect(() => {
    if (!companyId) return;
    supabase.from("companies").select("name").eq("id", companyId).maybeSingle().then(({ data }) => { setCompanyName(data?.name || ""); });
    fetchCrmContacts(); fetchDrivers(); fetchAssets();
  }, [companyId, fetchCrmContacts, fetchDrivers, fetchAssets]);

  useEffect(() => { if (session && companyId) fetchRoomData(); }, [session, companyId, fetchRoomData]);
  useEffect(() => { if (showEntryForm) { fetchCrmContacts(); fetchDrivers(); fetchAssets(); } }, [showEntryForm, fetchCrmContacts, fetchDrivers, fetchAssets]);

  // Jake delay monitor — compliance handled exclusively by useJakeIntelligence to avoid duplicates
  useEffect(() => {
    if (!companyId) return;
    runDelayMonitor(); const id = setInterval(runDelayMonitor, 5*60*1000); return () => clearInterval(id);
  }, [companyId, runDelayMonitor]);

  // Jake call state + client-side tool handlers (server tools handled by vapi-tools edge function)
  useEffect(() => {
    vapi.on('call-start', () => setIsJakeActive(true));
    vapi.on('call-end', () => setIsJakeActive(false));
    vapi.on('error', (err: any) => console.error('Jake error:', err));

    vapi.on('message', async (msg: any) => {
      if (msg?.type !== 'tool-calls') return;
      for (const tc of (msg.toolCallList || msg.toolCalls || [])) {
        const fn = tc?.function?.name || tc?.name;
        const args = (() => { try { return JSON.parse(tc?.function?.arguments || tc?.arguments || '{}'); } catch { return {}; } })();
        try {
          if (fn === 'update_load_status') {
            const { load_number, status } = args;
            const { data: found } = await supabase.from('loads').select('id').eq('company_id', companyId||'').ilike('load_number', String(load_number)).maybeSingle();
            if (!found) { (vapi as any).send({ type:'tool-call-result', toolCallId:tc.id, result:`Load ${load_number} not found.` }); continue; }
            const upd: any = { status };
            if (status === 'IN TRANSIT') upd.dispatched_at = new Date().toISOString();
            if (status === 'DELIVERED') upd.delivered_at = new Date().toISOString();
            await supabase.from('loads').update(upd).eq('id', found.id);
            fetchRoomData();
            (vapi as any).send({ type:'tool-call-result', toolCallId:tc.id, result:`Load ${load_number} updated to ${status}.` });
          } else if (fn === 'flag_load') {
            const { load_number, level, reason } = args;
            addJakeAlert({ level: level || 'YELLOW', message: reason || `Jake flagged load ${load_number}`, load_number });
            (vapi as any).send({ type:'tool-call-result', toolCallId:tc.id, result:`Alert created for load ${load_number}.` });
          } else if (fn === 'send_delay_notification') {
            const { load_number } = args;
            const { data: load } = await supabase.from('loads').select('*').eq('company_id', companyId||'').ilike('load_number', String(load_number)).maybeSingle();
            if (!load) { (vapi as any).send({ type:'tool-call-result', toolCallId:tc.id, result:`Load ${load_number} not found.` }); continue; }
            addJakeAlert({ level:'YELLOW', message:`Jake sending delay notification for Load #${load_number}`, load_number: String(load_number) });
            await supabase.functions.invoke('jake-delay-email', { body: { load, driverName: 'Driver', delayType: 'DELIVERY', hoursOverdue: 0 } });
            (vapi as any).send({ type:'tool-call-result', toolCallId:tc.id, result:`Delay notification sent for load ${load_number}.` });
          }
          // All other tools are server-side — handled by the vapi-tools edge function
        } catch (err: any) {
          try { (vapi as any).send({ type:'tool-call-result', toolCallId:tc.id, result:'I ran into an issue with that. Try again.' }); } catch {}
        }
      }
    });

    return () => { vapi.removeAllListeners?.(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  // ─── LOGIN / LOGOUT ───────────────────────────────────────────────────────
  const handleLogin = async () => {
    const email = loginEmail.trim(); const password = loginPassword;
    if (!email || !password) { alert("Enter email + password."); return; }
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) alert(error.message);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut(); setSession(null); setCheckingSession(false); window.location.reload();
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  if (checkingSession) return null;

  if (session && !companyId) {
    return (
      <div style={{ height:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:"#000", fontFamily:"monospace" }}>
        <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"3px", marginBottom:"16px" }}>BLACKTOP OS</div>
        <div style={{ color:"#555", fontSize:"12px", letterSpacing:"2px" }}>LOADING COMPANY...</div>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ height:"100vh", display:"flex", flexDirection:"column", gap:"12px", alignItems:"center", justifyContent:"center", background:"#000" }}>
        <div style={{ fontSize:"9px", letterSpacing:"0.2em", color:"#32CD32", marginBottom:"4px" }}>DISPATCH OS</div>
        <div style={{ fontSize:"22px", fontWeight:900, color:"#fff", letterSpacing:"4px", marginBottom:"20px" }}>BLACKTOP</div>
        <input style={{ ...inputStyle, width:360 }} placeholder="Email address" value={loginEmail} onChange={e=>setLoginEmail(e.target.value)} />
        <input style={{ ...inputStyle, width:360 }} type="password" placeholder="Password" value={loginPassword} onChange={e=>setLoginPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleLogin()} />
        <button onClick={handleLogin} style={{ background:"#32CD32", padding:"14px 40px", fontWeight:"bold", border:"none", cursor:"pointer", fontFamily:"monospace", letterSpacing:"2px", fontSize:"13px" }}>AUTHORIZE BLACKTOP OS</button>
        <div style={{ display:"flex", justifyContent:"space-between", width:360, marginTop:"8px" }}>
          <a href="/forgot" style={{ color:"#555", fontSize:"11px", textDecoration:"none" }}>Forgot password?</a>
          <a href="/signup" style={{ color:"#32CD32", fontSize:"11px", textDecoration:"none" }}>New carrier? Free trial →</a>
        </div>
      </div>
    );
  }

  const unackedAlerts = jakeAlerts.filter(a => !a.acknowledged);

  return (
    <div style={{ display:"flex", height:"100vh", background:"#000", color:"#fff", fontFamily:"monospace" }}>
      <button onClick={handleLogout} style={{ position:"absolute", top:10, right:10, zIndex:9999, background:"transparent", border:"1px solid #333", color:"#777", padding:"6px 12px", fontFamily:"monospace", fontSize:"10px", cursor:"pointer" }}>LOGOUT</button>

      {/* ── SIDEBAR ── */}
      <div style={{ width:"220px", background:"#080808", borderRight:"1px solid #1a1a1a", display:"flex", flexDirection:"column", flexShrink:0 }}>
        <div style={{ padding:"24px 20px 20px", borderBottom:"1px solid #1a1a1a" }}>
          <div style={{ fontSize:"9px", letterSpacing:"0.2em", color:"#32CD32", marginBottom:"4px" }}>DISPATCH OS</div>
          <div style={{ fontSize:"16px", fontWeight:900, color:"#fff", letterSpacing:"2px" }}>BLACKTOP</div>
          {companyName && <div style={{ fontSize:"10px", color:"#555", marginTop:"4px" }}>{companyName}</div>}
        </div>
        <nav style={{ flex:1, padding:"10px 0", overflowY:"auto" }}>
          {['Command Center','Control Tower','Driver Management','Assets / Equipment','Maintenance Hub','Compliance Room','Accounting','CRM','Fuel','Fleet LIVE GPS','Financials','Settings'].map(r => {
            const icons: Record<string,string> = { 'Command Center':'⬡','Control Tower':'🗼','Driver Management':'🚛','Assets / Equipment':'🔧','Maintenance Hub':'⚙️','Compliance Room':'📋','Accounting':'💰','CRM':'🤝','Fuel':'⛽','Fleet LIVE GPS':'📍','Financials':'📊','Settings':'⚙' };
            const active = room === r;
            return (
              <div key={r} onClick={() => { setRoom(r); setSelectedLoad(null); }}
                style={{ display:"flex", alignItems:"center", gap:"10px", padding:"10px 20px", fontSize:"11px", letterSpacing:"0.05em", fontWeight:active?700:400, color:active?"#32CD32":"#555", background:active?"rgba(50,205,50,0.07)":"transparent", borderLeft:active?"2px solid #32CD32":"2px solid transparent", cursor:"pointer" }}
                onMouseEnter={e=>{if(!active)(e.currentTarget as HTMLElement).style.color="#aaa";}}
                onMouseLeave={e=>{if(!active)(e.currentTarget as HTMLElement).style.color="#555";}}
              >
                <span style={{ fontSize:"13px", width:"16px", textAlign:"center", flexShrink:0 }}>{icons[r]}</span>
                {r.toUpperCase()}
              </div>
            );
          })}
        </nav>
        {unackedAlerts.length > 0 && (
          <div style={{ padding:"10px 14px", borderTop:"1px solid #1a1a1a", maxHeight:"180px", overflowY:"auto" }}>
            <div style={{ fontSize:"9px", letterSpacing:"1px", color:"#777", marginBottom:"6px" }}>JAKE ALERTS ({unackedAlerts.length})</div>
            {unackedAlerts.slice(0,3).map(a => (
              <div key={a.id} style={{ marginBottom:"6px", padding:"6px 8px", border:`1px solid ${a.level==='RED'?'#ff4444':a.level==='YELLOW'?'#FFD700':'#32CD32'}`, background:a.level==='RED'?'rgba(255,68,68,0.08)':'rgba(255,215,0,0.08)' }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span style={{ fontSize:"8px", fontWeight:700, color:a.level==='RED'?'#ff4444':a.level==='YELLOW'?'#FFD700':'#32CD32' }}>{a.level}</span>
                  <button onClick={()=>acknowledgeAlert(a.id)} style={{ background:"transparent", border:"none", color:"#555", cursor:"pointer", fontSize:"12px", padding:"0" }}>×</button>
                </div>
                <p style={{ margin:"2px 0 0", fontSize:"9px", color:"#ccc", lineHeight:"1.3" }}>{a.message.slice(0,60)}{a.message.length>60?'...':''}</p>
              </div>
            ))}
          </div>
        )}
        <div style={{ padding:"14px 20px", borderTop:"1px solid #1a1a1a" }}>
          <button onClick={toggleJake} style={isJakeActive ? voiceOn : voiceOff}>
            {isJakeActive ? '⬤ JAKE: LISTENING' : '○ ACTIVATE JAKE'}
          </button>
        </div>
      </div>

      {/* ── WORKSPACE ── */}
      <div style={{ flex:1, padding:"40px", overflowY:"auto", position:"relative" }}>
        <header style={{ marginBottom:"40px", borderBottom:"1px solid #1a1a1a", paddingBottom:"10px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h1 style={{ fontSize:"20px", color:"#32CD32", margin:0 }}>{room.toUpperCase()}</h1>
          <div style={{ display:"flex", gap:"10px" }}>
            {room === 'CRM' && !selectedLoad && (
              <button onClick={exportCrmCsv} style={{ ...smallBtn, background:"transparent", color:"#32CD32", border:"1px solid #32CD32" }}>EXPORT CSV</button>
            )}
            {room !== 'Command Center' && room !== 'Fleet LIVE GPS' && room !== 'Settings' && room !== 'Financials' && (
              <button onClick={() => setShowEntryForm(true)} style={smallBtn}>+ CREATE ENTRY</button>
            )}
          </div>
        </header>

        <div style={{ color:"#777", fontSize:"11px", marginBottom:"20px" }}>
          USER: {displayName || "Dispatcher"} | COMPANY: {companyName || "Loading..."}
        </div>

        {/* ── ROOM ROUTING ── */}
        {room === "Command Center" && <CommandCenter companyId={companyId} isJakeActive={isJakeActive} jakeAlerts={jakeAlerts} drivers={drivers} data={data} toggleJake={toggleJake} acknowledgeAlert={acknowledgeAlert} setRoom={setRoom} setSelectedLoad={setSelectedLoad} />}
        {room === 'Control Tower' && <ControlTower data={data} selectedLoad={selectedLoad} setSelectedLoad={setSelectedLoad} drivers={drivers} assets={assets} crmContacts={crmContacts} companyId={companyId} newLoad={newLoad} setNewLoad={setNewLoad} showEntryForm={showEntryForm} setShowEntryForm={setShowEntryForm} fetchRoomData={fetchRoomData} addJakeAlert={addJakeAlert} resolveAlertsForLoad={(loadNum: string) => setJakeAlerts(prev => prev.map(a => !a.acknowledged && String(a.load_number) === String(loadNum) && (a.message.includes('pickup') || a.message.includes('overdue')) ? { ...a, acknowledged: true } : a))} />}
        {room === 'Driver Management' && <DriverManagement data={data} selectedLoad={selectedLoad} setSelectedLoad={setSelectedLoad} companyId={companyId} newDriver={newDriver} setNewDriver={setNewDriver} showEntryForm={showEntryForm} setShowEntryForm={setShowEntryForm} fetchRoomData={fetchRoomData} />}
        {room === 'Assets / Equipment' && <AssetsEquipment data={data} selectedLoad={selectedLoad} setSelectedLoad={setSelectedLoad} companyId={companyId} newAsset={newAsset} setNewAsset={setNewAsset} showEntryForm={showEntryForm} setShowEntryForm={setShowEntryForm} fetchRoomData={fetchRoomData} />}
        {room === 'Compliance Room' && <ComplianceRoom data={data} drivers={drivers} assets={assets} companyId={companyId} newCompliance={newCompliance} setNewCompliance={setNewCompliance} showEntryForm={showEntryForm} setShowEntryForm={setShowEntryForm} fetchRoomData={fetchRoomData} />}
        {room === 'Accounting' && <Accounting data={data} selectedLoad={selectedLoad} setSelectedLoad={setSelectedLoad} companyId={companyId} newAccounting={newAccounting} setNewAccounting={setNewAccounting} showEntryForm={showEntryForm} setShowEntryForm={setShowEntryForm} fetchRoomData={fetchRoomData} />}
        {room === 'Fuel' && <Fuel data={data} drivers={drivers} companyId={companyId} newFuel={newFuel} setNewFuel={setNewFuel} showEntryForm={showEntryForm} setShowEntryForm={setShowEntryForm} fetchRoomData={fetchRoomData} />}
        {room === 'Maintenance Hub' && <MaintenanceHub data={data} assets={assets} companyId={companyId} newMaintenance={newMaintenance} setNewMaintenance={setNewMaintenance} showEntryForm={showEntryForm} setShowEntryForm={setShowEntryForm} fetchRoomData={fetchRoomData} />}
        {room === 'CRM' && <CRM data={data} selectedLoad={selectedLoad} setSelectedLoad={setSelectedLoad} companyId={companyId} crmDraft={crmDraft} setCrmDraft={setCrmDraft} showEntryForm={showEntryForm} setShowEntryForm={setShowEntryForm} fetchRoomData={fetchRoomData} exportCrmCsv={exportCrmCsv} />}
        {room === 'Fleet LIVE GPS' && <FleetGPS drivers={drivers} data={data} companyId={companyId} />}
        {room === 'Financials' && <Financials companyId={companyId} />}
        {room === 'Settings' && <Settings companyId={companyId} />}
      </div>
    </div>
  );
}
